import { TableCell, TableRow } from '@mui/material';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';

import { ResourceTimelineMessageListItemDto } from '@/api/generated';
import { DeleteIconButton } from '@/components/DeleteIconButton.comp.tsx';
import { ResourceTimelineMessagesTableRowMissingData } from '@/components/timelineMessages/comp/tableRow/components/ResourceTimelineMessagesTableRowMissingData.comp.tsx';
import { useDeleteResourceNote } from '@/components/timelineMessages/comp/tableRow/util/useDeleteResourceNote.util.ts';
import { DeleteDialog } from '@/components/ui/deleteDialog/DeleteDialog.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { formatDate } from '@/utils/formatDate.util.ts';
import { SanitizeHTML } from '@/utils/SanitzeHTML.comp.tsx';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';

type Props = {
  row: ResourceTimelineMessageListItemDto;
};

export const ResourceTimelineMessagesTableRowNote: React.FC<Props> = ({ row }) => {
  const { t } = useTranslationWithPrefix('components.timelineMessages');
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { deleteNote, isDeletingNote } = useDeleteResourceNote();

  const handleCloseDialog = () => {
    setIsDeleteDialogOpen(false);
  };

  const handleOpenDialog = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteNote = async () => {
    if (!row.note) {
      throw new Error('Note not available');
    }
    const { noteId, resourceId } = row.note;
    await deleteNote({ noteId, resource: resourceId });
  };

  if (!row.note) {
    return <ResourceTimelineMessagesTableRowMissingData type={row.type} />;
  }
  const { createdAt, creator, text } = row.note;

  return (
    <>
      <TableRow hover sx={{ border: `1px solid ${themeColors.stroke}`, py: 2 }}>
        <TableCell>
          <Stack spacing={1}>
            <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="f14Medium">{t(`type.${row.type}`)}</Typography>
              <DeleteIconButton isDeleting={isDeletingNote} onClick={handleOpenDialog} />
            </Stack>
            <Typography
              sx={{ color: themeColors.greyDark, wordBreak: 'break-word' }}
              variant="f16Regular"
            >
              <SanitizeHTML html={text} />
            </Typography>
            <Typography variant="f14Regular">
              <Stack
                direction="row"
                spacing={1}
                sx={{ color: themeColors.grey, justifyContent: 'end' }}
              >
                {`${creator.firstName} ${creator.lastName}`}
                <Box>|</Box>
                {formatDate(createdAt, { withTime: true })}
              </Stack>
            </Typography>
          </Stack>
        </TableCell>
      </TableRow>
      {isDeleteDialogOpen && (
        <DeleteDialog
          name={
            <>
              <div>{t(`type.${row.type}`)}</div>
              <Box sx={{ fontWeight: 'normal' }}>{<SanitizeHTML html={text} />}</Box>
            </>
          }
          onCancel={handleCloseDialog}
          onConfirm={handleDeleteNote}
          title={t('deleteDialog.title')}
        />
      )}
    </>
  );
};
